import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { interviewAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { TrendingUp, AlertCircle, CheckCircle, XCircle, Award, ArrowLeft } from 'lucide-react';

const InterviewFeedback = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeedback();
  }, [sessionId]);

  const loadFeedback = async () => {
    try {
      const sessionData = await interviewAPI.getSession(sessionId);
      setSession(sessionData);
      
      if (sessionData.status === 'completed' && sessionData.feedback) {
        setFeedback(sessionData.feedback);
      } else {
        // Complete interview if not already completed
        const feedbackData = await interviewAPI.completeInterview(sessionId);
        setFeedback(feedbackData);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load interview feedback',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-xl text-gray-700">Generating comprehensive feedback...</div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <Card>
          <CardContent className="p-8 text-center">
            <p>No feedback available</p>
            <Button onClick={() => navigate('/interview')} className="mt-4">
              Back to Interview
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getApprovalColor = (likelihood) => {
    if (likelihood === 'high') return 'bg-green-100 text-green-800 border-green-300';
    if (likelihood === 'medium') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/interview/history')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to History
          </Button>
          <div className="text-center">
            <div className="inline-block mb-4 px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full text-white text-sm font-semibold">
              🎓 Interview Complete
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
              Your Interview Feedback
            </h1>
            <p className="text-gray-600">Comprehensive analysis and recommendations</p>
          </div>
        </div>

        {/* Overall Score */}
        <Card className="mb-8 bg-gradient-to-br from-white to-emerald-50 border-2 border-emerald-200 shadow-xl">
          <CardContent className="p-8 text-center">
            <Award className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Overall Score</h2>
            <div className={`text-6xl font-bold ${getScoreColor(feedback.overall_score)} mb-4`}>
              {feedback.overall_score}/100
            </div>
            <div className="max-w-md mx-auto">
              <Progress value={feedback.overall_score} className="h-3" />
            </div>
          </CardContent>
        </Card>

        {/* Approval Likelihood */}
        {feedback.estimated_approval_likelihood && (
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Estimated Visa Approval Likelihood</h3>
                  <p className="text-gray-600">Based on your interview performance</p>
                </div>
                <Badge className={`text-lg px-6 py-2 ${getApprovalColor(feedback.estimated_approval_likelihood)}`}>
                  {feedback.estimated_approval_likelihood.toUpperCase()}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Content Assessment */}
          {feedback.content_assessment && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                  Content Assessment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(feedback.content_assessment).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between mb-2">
                      <span className="capitalize text-gray-700">{key.replace('_', ' ')}</span>
                      <span className="font-semibold">{value}/10</span>
                    </div>
                    <Progress value={value * 10} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Communication Assessment */}
          {feedback.communication_assessment && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
                  Communication Assessment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(feedback.communication_assessment).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between mb-2">
                      <span className="capitalize text-gray-700">{key.replace('_', ' ')}</span>
                      <span className="font-semibold">
                        {typeof value === 'number' ? `${value}/10` : value}
                      </span>
                    </div>
                    {typeof value === 'number' && <Progress value={value * 10} className="h-2" />}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Strengths */}
        {feedback.strengths && feedback.strengths.length > 0 && (
          <Card className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center text-green-800">
                <CheckCircle className="h-5 w-5 mr-2" />
                Your Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {feedback.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span className="text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Areas for Improvement */}
        {feedback.areas_for_improvement && feedback.areas_for_improvement.length > 0 && (
          <Card className="mb-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200">
            <CardHeader>
              <CardTitle className="flex items-center text-yellow-800">
                <AlertCircle className="h-5 w-5 mr-2" />
                Areas for Improvement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {feedback.areas_for_improvement.map((area, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    <span className="text-gray-700">{area}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Red Flags */}
        {feedback.red_flags && feedback.red_flags.length > 0 && (
          <Card className="mb-6 bg-gradient-to-br from-red-50 to-pink-50 border-2 border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center text-red-800">
                <XCircle className="h-5 w-5 mr-2" />
                Areas of Concern
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {feedback.red_flags.map((flag, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-600 mr-2">⚠️</span>
                    <span className="text-gray-700">{flag}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        {feedback.recommendations && feedback.recommendations.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                Actionable Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {feedback.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}

        {/* Summary */}
        {feedback.summary && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Overall Assessment</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">{feedback.summary}</p>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex justify-center space-x-4">
          <Button
            onClick={() => navigate('/interview')}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          >
            Start New Interview
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/interview/history')}
          >
            View History
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InterviewFeedback;